import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { GET_PIE_CHART } from "../../../../services/ApiCalls";
import { checkResponse } from "../../../../utilities/utilities";
import { useTranslation } from "react-i18next";

const RevenueChart = () => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState("today");
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  const periodTabs = [
    { key: "today", label: t("dashboard.today") },
    { key: "yesterday", label: t("dashboard.yesterday") },
    { key: "thisWeek", label: t("dashboard.thisWeek") },
    { key: "thisMonth", label: t("dashboard.thisMonth") },
    { key: "lastMonth", label: t("dashboard.lastMonth") },
  ];

  const { series, options, bookingTypes } = useMemo(() => {
    const colorsArr = [
      "#e8307a",
      "#ff5c5c",
      "#09a86a",
      "#4c5eff",
      "#ffa500",
      "#0088cc",
    ];

    const labels = chartData.map((d) => d.label);
    const seriesData = chartData.map((d) => d.percentage);

    return {
      series: seriesData.length ? seriesData : [0],
      bookingTypes: chartData.map((item, i) => ({
        type: item.label,
        percentage: item.percentage,
        color: colorsArr[i % colorsArr.length],
      })),
      options: {
        chart: { type: "donut", height: 300 },
        labels: labels.length ? labels : [t("dashboard.common.noDataAvailable")],
        colors: colorsArr,
        legend: {
          show: true,
          position: "bottom",
          fontSize: "12px",
          markers: { width: 10, height: 10, radius: 50 },
        },
        stroke: {
          width: 2,
          colors: ["#fff"],
        },
        plotOptions: {
          pie: {
            donut: { size: "60%" },
          },
        },
        dataLabels: { enabled: false },
      },
    };
  }, [chartData, t]);

  useEffect(() => {
    setLoading(true);
    GET_PIE_CHART({ filter: activeTab }).then((res) => {
      checkResponse({
        res,
        setData: (data) => {
          setChartData(data || []);
          setLoading(false);
        },
        setLoader: setLoading,
      });
    });
  }, [activeTab]);

  return (
    <div
      className="p-3 shadow-sm rounded-4 bg-white"
      style={{ border: "1px solid #f1f1f1" }}
    >
      <h6 className="fw-bold mb-2" style={{ color: "#333" }}>
        Stay Duration Distribution
      </h6>

      <div className="d-flex gap-3 mb-3 flex-wrap">
        {periodTabs.map((tab) => (
          <span
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: activeTab === tab.key ? "600" : "normal",
              color: activeTab === tab.key ? "#E8307A" : "#6C757D",
              borderBottom:
                activeTab === tab.key ? "2px solid #E8307A" : "none",
              paddingBottom: "2px",
            }}
          >
            {tab.label}
          </span>
        ))}
      </div>

      <div className="row align-items-center">
        <div className="col-md-6 text-center">
          {series?.length ? (
            <ReactApexChart
              key={`${activeTab}-${series.join("-")}`}
              options={options}
              series={series}
              type="donut"
              height={260}
            />
          ) : (
            <div className="text-muted py-5">{t("dashboard.common.noDataAvailable")}</div>
          )}
        </div>

        <div className="col-md-6">
          {bookingTypes.length > 0 ? (
            bookingTypes.map((booking) => (
              <div
                key={booking.type}
                className="d-flex align-items-center mb-3"
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: booking.color,
                    marginRight: "8px",
                  }}
                />
                <span
                  className="fw-bold me-auto"
                  style={{ fontSize: "14px" }}
                >
                  {booking.type}
                </span>
                <span
                  className="fw-bold"
                  style={{ fontSize: "14px", color: booking.color }}
                >
                  {booking.percentage}%
                </span>
              </div>
            ))
          ) : (
            <div className="text-muted small">
              {t("dashboard.common.noDataAvailable")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueChart;
